import React, { useState, useEffect } from "react";
import { Button, Modal, Tabs, Empty, Spin } from "antd";
import { InboxOutlined, WechatOutlined, UploadOutlined, CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { _resolveMediaUrl } from "./MessageParts";

// `uploadStatus` (undefined | "uploading" | "done" | "error") is set by the
// caller directly on each staged image object once it starts submitting —
// this component just renders whatever's there, it never sets it itself.
export function GalleryPickerThumb({ item, selected, selectionIndex, onToggle, disabled, showSourceIcon = false, uploadStatus }) {
  const [url, setUrl] = useState(item.source === "upload" ? item.previewUrl : null);
  useEffect(() => {
    if (item.source === "upload") return;
    if (!item?.data) return;
    _resolveMediaUrl(item.data).then(setUrl).catch(() => {});
  }, [item?.data, item?.source]);

  const isChat = item.source === "chat";

  return (
    <div
      onClick={() => (!disabled || selected) && onToggle?.()}
      style={{
        position: "relative", aspectRatio: "1", borderRadius: 6, overflow: "hidden",
        cursor: disabled && !selected ? "not-allowed" : (onToggle ? "pointer" : "default"),
        border: selected ? "3px solid #1677ff" : "2px solid transparent",
        background: "#f0f0f0", flexShrink: 0,
        transition: "border-color 0.15s",
        opacity: disabled && !selected ? 0.45 : 1,
      }}
    >
      {url
        ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Spin size="small" /></div>
      }
      {selected && !uploadStatus && (
        <div style={{
          position: "absolute", top: 4, right: 4,
          width: 20, height: 20, borderRadius: "50%",
          background: "#1677ff", color: "#fff",
          fontSize: 11, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }}>
          {selectionIndex}
        </div>
      )}
      {uploadStatus && (
        <div style={{
          position: "absolute", inset: 0,
          background: uploadStatus === "uploading" ? "rgba(0,0,0,0.35)" : "transparent",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {uploadStatus === "uploading" && <Spin size="small" style={{ filter: "invert(1)" }} />}
          {uploadStatus === "done" && (
            <CheckCircleFilled style={{ position: "absolute", top: 4, right: 4, fontSize: 18, color: "#52c41a", background: "#fff", borderRadius: "50%" }} />
          )}
          {uploadStatus === "error" && (
            <CloseCircleFilled style={{ position: "absolute", top: 4, right: 4, fontSize: 18, color: "#ff4d4f", background: "#fff", borderRadius: "50%" }} />
          )}
        </div>
      )}
      {showSourceIcon && !uploadStatus && (
        <div style={{
          position: "absolute", bottom: 3, left: 3,
          background: "rgba(0,0,0,0.55)", borderRadius: 4,
          padding: "1px 4px", display: "flex", alignItems: "center",
        }}>
          {isChat
            ? <WechatOutlined style={{ fontSize: 10, color: "#fff" }} />
            : <UploadOutlined style={{ fontSize: 10, color: "#fff" }} />
          }
        </div>
      )}
    </div>
  );
}

const MAX_IMAGES = 10;

// Staged-image picker shared by the Create Query form (CustomerEditDrawer)
// and the Parts-tab "Add photos" flow (WorkflowTaskDrawer) — pick from the
// customer's chat images or upload from device, staged locally as
// {id, source: "upload"|"chat", file/data, previewUrl, name}. No network
// call happens here; the caller uploads staged images (and does whatever it
// does with the result) only when its own save/submit runs.
export function QueryImagePicker({ images, setImages, imageMessages = [], imageMessagesLoading = false, max = MAX_IMAGES, label = "Images" }) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryModalTab, setGalleryModalTab] = useState("chat");

  function processUploadedFiles(files) {
    files.slice(0, max).forEach(file => {
      const reader = new FileReader();
      reader.onload = e => {
        setImages(prev => {
          if (prev.length >= max) return prev;
          return [...prev, {
            id: `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            source: "upload",
            file,
            previewUrl: e.target.result,
            name: file.name,
          }];
        });
      };
      reader.readAsDataURL(file);
    });
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 12, color: "#8c8c8c" }}>
          {label}
          {images.length > 0 && (
            <span style={{ marginLeft: 6, color: "#1677ff", fontWeight: 600 }}>
              {images.length} / {max}
            </span>
          )}
        </div>
        <Button size="small" onClick={() => { setGalleryModalTab("chat"); setGalleryOpen(true); }}>
          Add Images
        </Button>
      </div>
      {images.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4 }}>
          {images.map((img, i) => (
            <GalleryPickerThumb
              key={img.id}
              item={img}
              selected
              selectionIndex={i + 1}
              showSourceIcon
              disabled={false}
              uploadStatus={img.uploadStatus}
              onToggle={img.uploadStatus ? undefined : () => setImages(prev => prev.filter(x => x.id !== img.id))}
            />
          ))}
        </div>
      )}

      {/* Image picker modal */}
      <Modal
        open={galleryOpen}
        onCancel={() => setGalleryOpen(false)}
        footer={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "#8c8c8c" }}>{images.length} / {max} selected</span>
            <Button type="primary" onClick={() => setGalleryOpen(false)}>Done</Button>
          </div>
        }
        title="Add Images"
        width={500}
        styles={{ body: { padding: 0 } }}
        destroyOnHidden={false}
      >
        <Tabs
          activeKey={galleryModalTab}
          onChange={setGalleryModalTab}
          size="small"
          tabBarStyle={{ margin: 0, paddingLeft: 16, paddingRight: 16 }}
          items={[
            {
              key: "chat",
              label: `From Chat${imageMessages.length ? ` (${imageMessages.length})` : ""}`,
              children: (
                <div style={{ padding: "12px 16px", maxHeight: 380, overflowY: "auto" }}>
                  {imageMessagesLoading ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, margin: "32px 0" }}>
                      <Spin />
                      <span style={{ fontSize: 12, color: "#8c8c8c" }}>Loading chat images…</span>
                    </div>
                  ) : imageMessages.length === 0 ? (
                    <Empty description="No images in this chat" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "24px 0" }} />
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                      {imageMessages.map(img => {
                        const enriched = { ...img, source: "chat" };
                        const isSelected = images.some(x => x.id === img.id);
                        const selectionIndex = images.findIndex(x => x.id === img.id) + 1;
                        const atLimit = images.length >= max;
                        return (
                          <GalleryPickerThumb
                            key={img.id}
                            item={enriched}
                            selected={isSelected}
                            selectionIndex={isSelected ? selectionIndex : 0}
                            disabled={atLimit && !isSelected}
                            onToggle={() => setImages(prev => {
                              if (isSelected) return prev.filter(x => x.id !== img.id);
                              if (prev.length >= max) return prev;
                              return [...prev, enriched];
                            })}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: "upload",
              label: "Upload from Device",
              children: (
                <div style={{ padding: "12px 16px" }}>
                  <label style={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    border: "1px dashed #d9d9d9", borderRadius: 8, padding: "24px 16px",
                    cursor: images.length >= max ? "not-allowed" : "pointer",
                    background: "#fafafa", marginBottom: 12,
                    opacity: images.length >= max ? 0.5 : 1,
                    transition: "border-color 0.2s",
                  }}
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#1677ff"; }}
                    onDragLeave={e => { e.currentTarget.style.borderColor = "#d9d9d9"; }}
                    onDrop={e => {
                      e.preventDefault();
                      e.currentTarget.style.borderColor = "#d9d9d9";
                      if (images.length >= max) return;
                      const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith("image/"));
                      processUploadedFiles(files);
                    }}
                  >
                    <input
                      type="file" multiple accept="image/*" style={{ display: "none" }}
                      disabled={images.length >= max}
                      onChange={e => { processUploadedFiles(Array.from(e.target.files)); e.target.value = ""; }}
                    />
                    <InboxOutlined style={{ fontSize: 32, color: "#8c8c8c", marginBottom: 8 }} />
                    <div style={{ fontSize: 13, color: "#595959", fontWeight: 500 }}>Click or drag images here</div>
                    <div style={{ fontSize: 11, color: "#bfbfbf", marginTop: 4 }}>
                      {images.length >= max ? `Limit reached (${max})` : `Up to ${max - images.length} more`}
                    </div>
                  </label>
                  {images.filter(i => i.source === "upload").length > 0 && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
                      {images.filter(i => i.source === "upload").map(img => (
                        <GalleryPickerThumb
                          key={img.id}
                          item={img}
                          selected
                          selectionIndex={images.findIndex(x => x.id === img.id) + 1}
                          disabled={false}
                          onToggle={() => setImages(prev => prev.filter(x => x.id !== img.id))}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
}
